import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../lib/axios';
import { useToast } from "../../hooks/use-toast";

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Alert,
  AlertDescription,
} from "../../components/ui/alert";
import {
  ArrowLeftIcon,
  Pencil1Icon,
  Cross2Icon,
  CheckIcon,
} from '@radix-ui/react-icons';
import SearchSelectCompanies from '@/components/SearchSelectCompanies/SearchSelectCompanies';
import UserStatus from './UserStatus';

const UserPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const capFirstLetterAndLowerCaseRest = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/users/${id}`);
        setUser(response.data);
        setFormData(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
        toast({
          title: "Error",
          description: "Failed to load user details. Please try again.",
          variant: "destructive",
        });
        navigate('/users');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, navigate, toast]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCompanyChange = (value) => {
    setFormData(prev => ({
        ...prev,
        company_id: value[0]?.id,
        company: {
            id: value[0]?.id,
            name: value[0]?.name,
        },
      }));
  }

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name?.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name?.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const payload = formData;

    if (!payload.company_id) {
        payload.company_id = null;
    }

    setSaving(true);
    try {
      const response = await axios.put(`/users/${id}`, payload);
      setUser(response.data);
      setIsEditing(false);
      toast({
        title: "Success",
        description: "User updated successfully",
      });
    } catch (error) {
      console.error('Error updating user:', error);
      if (error.response?.data?.error === 'User with this email already exists') {
        setErrors(prev => ({
          ...prev,
          email: 'Email is already in use'
        }));
      } else {
        toast({
          title: "Error",
          description: "Failed to update user. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full min-w-full flex flex-col">
        <Card className="w-full max-w-7xl mx-auto">
          <CardHeader>
            <Skeleton className="h-8 w-48" />
          </CardHeader>
          <CardContent className="space-y-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full min-w-full flex flex-col">
      <Card className="w-full max-w-7xl mx-auto">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/users')}
              className="flex items-center gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Users
            </Button>
            {!isEditing && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2"
                >
                  <Pencil1Icon className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          <CardTitle className="text-3xl font-bold text-center">User Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              {isEditing ? (
                <>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name || ''}
                    onChange={handleInputChange}
                    className={errors.first_name ? 'border-red-500' : ''}
                  />
                  {errors.first_name && (
                    <Alert variant="destructive" className="py-2">
                      <AlertDescription>{errors.first_name}</AlertDescription>
                    </Alert>
                  )}
                </>
              ) : (
                <div className="p-2 border rounded-md">
                  {capFirstLetterAndLowerCaseRest(user.first_name)}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              {isEditing ? (
                <>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name || ''}
                    onChange={handleInputChange}
                    className={errors.last_name ? 'border-red-500' : ''}
                  />
                  {errors.last_name && (
                    <Alert variant="destructive" className="py-2">
                      <AlertDescription>{errors.last_name}</AlertDescription>
                    </Alert>
                  )}
                </>
              ) : (
                <div className="p-2 border rounded-md">
                  {capFirstLetterAndLowerCaseRest(user.last_name)}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            {isEditing ? (
              <>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  disabled
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <Alert variant="destructive" className="py-2">
                    <AlertDescription>{errors.email}</AlertDescription>
                  </Alert>
                )}
              </>
            ) : (
              <div className="p-2 border rounded-md">
                {user.email}
              </div>
            )}
          </div>

          {formData.role !== 'admin' && 
            <div className="space-y-2">
                <Label htmlFor="company_id">Company</Label>
                {isEditing ? (
                <>
                    <SearchSelectCompanies isSingle values={ formData.company_id ? [{id: formData.company_id, name:  formData?.company?.name} ] : []} onChange={handleCompanyChange}/>
                    {errors.company_id && (
                    <Alert variant="destructive" className="py-2">
                        <AlertDescription>{errors.company_id}</AlertDescription>
                    </Alert>
                    )}
                </>
                ) : (
                <div className="p-2 border rounded-md">
                    {user?.company?.name}
                    {user.company?.id && 
                        <Badge className="bg-blue-500 ml-2" >
                        <span className='font-semibold'>ID: </span>
                        {user?.company?.id}
                    </Badge>}
                </div>
                )}
            </div>
          }

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="mr-2">Role</Label>
              {isEditing ? (
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="secondary">
                  {capFirstLetterAndLowerCaseRest(user.role)}
                </Badge>
              )}
            </div>

            <div className="space-y-2">
              <Label className="mr-2">Status</Label>
              {isEditing ? (
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                  disabled={formData.status === 'invited'}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="invited" disabled>Invited</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <UserStatus status={formData.status}/>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setFormData(user);
                  setErrors({});
                }}
                disabled={saving}
              >
                <Cross2Icon className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}

          <div className="text-sm text-gray-500">
            <p>Created: {new Date(user.created_at).toLocaleString()}</p>
            <p>Last Updated: {new Date(user.updated_at).toLocaleString()}</p>
            <p>Clerk ID: {user.clerk_id}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserPage;